import React, { useRef, useState, useCallback, useEffect } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { useConfig } from '../ConfigProvider'
import cloneDeep from 'lodash/cloneDeep'
import FileItem from './FileItem'
import Button from '../Buttons'
import CloseButton from '../CloseButton'
import Grid from '@mui/system/Unstable_Grid/Grid'
import { isValidArray } from 'utils/helper'
import { COLORS } from '../utils/constant'
import Tooltip from '../Tooltip'
import useBoolean from 'utils/hooks/useBoolean'

const filesToArray = (files) => Object.keys(files).map((key) => files[key])

const Upload = React.forwardRef((props, ref) => {
    const {
        accept,
        beforeUpload,
        disabled,
        draggable,
        fileList,
        multiple,
        onChange,
        onFileRemove,
        showList,
        tip,
        uploadLimit,
        children,
        className,
        field,
        form,
        fullWidth = true,
        uploadButtonStyle = {},
        onlyView = false,
        ...rest
    } = props

    const fileInputField = useRef(null)
    const [files, setFiles] = useState(fileList)
    const [dragOver, setDragOver] = useState(false)
    const [openTooltip, setOpenTooltip] = useBoolean(false)

    const moreFilesCount = files?.slice(1).length

    const { themeColor, primaryColorLevel } = useConfig()

    useEffect(() => {
        setFiles(fileList)
    }, [fileList])

    const triggerMessage = (msg) => {
        // toast.push(
        //     <Notification type="danger" duration={2000}>
        //         {msg || 'Upload Failed!'}
        //     </Notification>,
        //     {
        //         placement: 'top-center',
        //     }
        // )
        console.log(msg || 'Upload Failed!')
    }

    const pushFile = (newFiles, file) => {
        for (let f of newFiles) {
            file.push(f)
        }
        return file
    }

    const addNewFiles = (newFiles) => {
        let file = cloneDeep(files)
        if (typeof uploadLimit === 'number' && uploadLimit !== 0) {
            if (Object.keys(file).length >= uploadLimit) {
                if (uploadLimit === 1) {
                    file.shift()
                    file = pushFile(newFiles, file)
                }

                return filesToArray({ ...file })
            }
        }
        file = pushFile(newFiles, file)
        return filesToArray({ ...file })
    }

    const onNewFileUpload = (e) => {
        const { files: newFiles } = e.target
        let result = true

        if (beforeUpload) {
            result = beforeUpload(newFiles, files)

            if (result === false) {
                triggerMessage()
                return
            }

            if (typeof result === 'string' && result.length > 0) {
                triggerMessage(result)
                return
            }
        }

        if (result) {
            let updatedFiles = addNewFiles(newFiles)
            setFiles(updatedFiles)
            onChange?.(updatedFiles, files)
        }
    }

    const removeFile = (fileIndex) => {
        if (!onlyView) {
            const deletedFileList = files.filter(
                (_, index) => index !== fileIndex
            )
            setFiles(deletedFileList)
            onFileRemove?.(deletedFileList)
        }
    }

    const triggerUpload = (e) => {
        if (!disabled) {
            fileInputField.current?.click()
        }
        e.stopPropagation()
    }

    const renderChildren = () => {
        if (!draggable && !children) {
            return (
                <Button
                    className={`text-theme-color border-theme-color max-h-10 ${
                        !fullWidth
                            ? 'px-2 !important -ml-4 max-h-8 w-16'
                            : 'ml-4'
                    }`}
                    style={{
                        paddingLeft: !fullWidth ? 0 : '2rem',
                        paddingRight: !fullWidth ? 0 : '2rem',
                        maxHeight: '2rem',
                        ...uploadButtonStyle,
                    }}
                    disabled={disabled}
                    onClick={(e) => e.preventDefault()}
                >
                    Browse
                </Button>
            )
        }

        if (draggable && !children) {
            return <span>Choose a file or drag and drop here</span>
        }

        return children
    }

    const handleDragLeave = useCallback(() => {
        if (draggable) {
            setDragOver(false)
        }
    }, [draggable])

    const handleDragOver = useCallback(() => {
        if (draggable && !disabled) {
            setDragOver(true)
        }
    }, [draggable, disabled])

    const handleDrop = useCallback(() => {
        if (draggable) {
            setDragOver(false)
        }
    }, [draggable])

    const draggableProp = {
        onDragLeave: handleDragLeave,
        onDragOver: handleDragOver,
        onDrop: handleDrop,
    }

    const draggableEventFeedbackClass = `border-${themeColor}-${primaryColorLevel}`

    const uploadClass = classNames(
        'ml-2',
        'upload',
        draggable && `upload-draggable`,
        draggable && !disabled && `hover:${draggableEventFeedbackClass}`,
        draggable && disabled && 'disabled',
        dragOver && draggableEventFeedbackClass,
        className
    )

    const uploadInputClass = classNames(
        'upload-input',
        draggable && `draggable`
    )

    const getToolTipTitle = () => {
        return files?.slice(1)?.map((file, ind) => (
            <FileItem
                file={file}
                key={(file.name || file.fileName) + ind}
                isFileInTooltip={true}
                setOpenTooltip={setOpenTooltip}
            >
                <CloseButton
                    onClick={() => removeFile(ind + 1)}
                    className={`upload-file-remove h-2 -mt-2 ${
                        onlyView
                            ? 'text-gray-300 pointer-events-none invisible'
                            : 'text-white'
                    }`}
                    size={15}
                />
            </FileItem>
        ))
    }

    return (
        <>
            <Grid
                container
                className="border border-theme-light-gray pl-2 pt-2 h-12 rounded-md"
            >
                <Grid item xs={12} md={10}>
                    {tip}
                    {showList && (
                        <div className="upload-file-list mt-0">
                            <Grid container>
                                {isValidArray(files) ? (
                                    files.slice(0, 1).map((file, index) => (
                                        <div className="upload-file border-0 mr-6">
                                            <FileItem
                                                file={file}
                                                key={
                                                    (file.name ||
                                                        file.fileName) + index
                                                }
                                            >
                                                <CloseButton
                                                    onClick={() =>
                                                        removeFile(index)
                                                    }
                                                    className={`upload-file-remove ${
                                                        onlyView
                                                            ? 'text-gray-300 pointer-events-none invisible'
                                                            : 'text-theme-color'
                                                    }`}
                                                    size={20}
                                                    disabled={true}
                                                />
                                            </FileItem>
                                            {moreFilesCount > 0 && (
                                                <Tooltip
                                                    isPlainTooltip={false}
                                                    title={getToolTipTitle()}
                                                    className="bg-theme-color"
                                                    isOpen={openTooltip}
                                                >
                                                    <span
                                                        className="bg-theme-border-light-color text-theme-color text-sm border-0 max-h-8 rounded-md p-3 ml-2 -mt-2 cursor-pointer pt-0"
                                                        onClick={() =>
                                                            setOpenTooltip.toggle()
                                                        }
                                                    >
                                                        + {moreFilesCount}
                                                        <CloseButton
                                                            onClick={(e) => {
                                                                setOpenTooltip.off()
                                                                e.stopPropagation()
                                                            }}
                                                            iconClass="-mt-5 ml-7 text-theme-color"
                                                            size={18}
                                                        />
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-theme-light-gray mt-2">
                                        {onlyView
                                            ? 'No Attachment'
                                            : 'Upload document'}
                                    </p>
                                )}
                            </Grid>
                        </div>
                    )}
                </Grid>
                {!onlyView && (
                    <Grid item xs={12} md={2}>
                        <div
                            ref={ref}
                            className={uploadClass}
                            {...(draggable
                                ? draggableProp
                                : { onClick: triggerUpload })}
                            {...rest}
                        >
                            <input
                                className={uploadInputClass}
                                type="file"
                                ref={fileInputField}
                                onChange={onNewFileUpload}
                                disabled={disabled}
                                multiple={multiple}
                                accept={accept}
                                title=""
                                value=""
                                {...field}
                                {...rest}
                            ></input>
                            {renderChildren()}
                        </div>
                    </Grid>
                )}
            </Grid>
        </>
    )
})

Upload.propTypes = {
    uploadLimit: PropTypes.number,
    draggable: PropTypes.bool,
    disabled: PropTypes.bool,
    showList: PropTypes.bool,
    multiple: PropTypes.bool,
    accept: PropTypes.string,
    tip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
}

Upload.defaultProps = {
    draggable: false,
    showList: true,
    disabled: false,
    fileList: [],
}

export default Upload
